/**
 * parseParkedSchemaStatement — lightweight lexical scan that pulls the
 * schema name + optional AUTHORIZATION owner out of the parked statement
 * text so the inline `[Edit Schema]` popover can seed its form.
 *
 * DSE2's `extractDdlStructure` does not expose a structured schema arm,
 * so the inline path scans the parked statement directly.
 *
 * Accepts:
 *   - CREATE SCHEMA [IF NOT EXISTS] <name> [AUTHORIZATION <owner>]
 *   - ALTER SCHEMA <name> RENAME TO <new>
 *
 * PRD: Ralph/VqeDdlEdit/PRD.md § Slice D5 — Schema UI.
 */

const IDENT_RE_SOURCE = '[A-Za-z_][A-Za-z0-9_]*|"[^"]+"|`[^`]+`|\\[[^\\]]+\\]';
const IDENT_RE = new RegExp(`^(${IDENT_RE_SOURCE})`);

export interface ParsedParkedSchema {
  readonly action: 'create' | 'alter';
  readonly name: string;
  readonly quoted: boolean;
  readonly ifNotExists: boolean;
  readonly authorization?: string;
  readonly renameTo?: string;
}

function unquoteIdent(raw: string): { name: string; quoted: boolean } {
  if (raw.length >= 2) {
    const first = raw.charAt(0);
    const last = raw.charAt(raw.length - 1);
    if ((first === '"' && last === '"') || (first === '`' && last === '`')) {
      return { name: raw.slice(1, -1), quoted: true };
    }
    if (first === '[' && last === ']') {
      return { name: raw.slice(1, -1), quoted: true };
    }
  }
  return { name: raw, quoted: false };
}

function skipSpaces(source: string, start: number): number {
  let i = start;
  while (i < source.length && /\s/.test(source.charAt(i))) i++;
  return i;
}

export function parseParkedSchemaStatement(stmtText: string): ParsedParkedSchema | null {
  const trimmed = stmtText.trim();
  const upper = trimmed.toUpperCase();

  let action: 'create' | 'alter';
  let cursor: number;
  if (upper.startsWith('CREATE')) { action = 'create'; cursor = 'CREATE'.length; }
  else if (upper.startsWith('ALTER')) { action = 'alter'; cursor = 'ALTER'.length; }
  else return null;

  cursor = skipSpaces(trimmed, cursor);
  if (!upper.startsWith('SCHEMA', cursor)) return null;
  cursor += 'SCHEMA'.length;
  cursor = skipSpaces(trimmed, cursor);

  let ifNotExists = false;
  if (action === 'create' && upper.startsWith('IF', cursor)) {
    const m = /^IF\s+NOT\s+EXISTS\s+/.exec(upper.slice(cursor));
    if (m) {
      ifNotExists = true;
      cursor += m[0].length;
    }
  }

  const nameMatch = IDENT_RE.exec(trimmed.slice(cursor));
  if (!nameMatch) return null;
  const ident = unquoteIdent(nameMatch[1]!);
  cursor += nameMatch[0].length;
  cursor = skipSpaces(trimmed, cursor);

  if (action === 'create') {
    let authorization: string | undefined;
    if (upper.startsWith('AUTHORIZATION', cursor)) {
      cursor += 'AUTHORIZATION'.length;
      cursor = skipSpaces(trimmed, cursor);
      const ownerMatch = IDENT_RE.exec(trimmed.slice(cursor));
      if (ownerMatch) {
        authorization = unquoteIdent(ownerMatch[1]!).name;
      }
    }
    return {
      action,
      name: ident.name,
      quoted: ident.quoted,
      ifNotExists,
      ...(authorization !== undefined ? { authorization } : {}),
    };
  }

  // alter — RENAME TO <new>
  let renameTo: string | undefined;
  if (upper.startsWith('RENAME', cursor)) {
    const m = /^RENAME\s+TO\s+/.exec(upper.slice(cursor));
    if (m) {
      cursor += m[0].length;
      const newMatch = IDENT_RE.exec(trimmed.slice(cursor));
      if (newMatch) renameTo = unquoteIdent(newMatch[1]!).name;
    }
  }
  return {
    action,
    name: ident.name,
    quoted: ident.quoted,
    ifNotExists: false,
    ...(renameTo !== undefined ? { renameTo } : {}),
  };
}
